import { Component, computed, input, output } from '@angular/core';

import { TranslateModule } from '@ngx-translate/core';

/**
 * Cart actions of a list-mode product that is already in the cart.
 * One build: "Edit" above "Add new". Several builds (different orderLineBuilderId): how many there are,
 * "Show in cart" and "Add new" stacked, since a single counter or edit button can't address one of them.
 * totalCount replaces the number of builds in that summary with a quantity (a pieces-group variant in several builds).
 * Look and theme hooks: libs/web-ui/src/styles/themable/_product-list.scss.
 */
@Component({
  standalone: true,
  selector: 'product-cart-actions',
  imports: [TranslateModule],
  host: { 'data-modal-ignore': '' },
  template: `
    @if (isMultiBuild()) {
      <div class="ws-product-list__cart-actions ws-product-list__cart-actions--multi">
        <div class="ws-product-list__cart-summary">
          @if (totalCount() !== null) {
            {{ 'productsList.productCard.totalInCart' | translate: { count: totalCount() } }}
          } @else {
            {{ 'productsList.productCard.variantsInCart' | translate: { count: buildsCount() } }}
          }
        </div>
        <!-- own box: the buttons share the widest one's width, not the summary's -->
        <div class="ws-product-list__cart-buttons">
          <!-- cart-button-toggler: the opening click still reaches document, and the cart's click-outside would
               close it again at once unless the click came from a cart toggler -->
          <button type="button"
                  class="btn ws-button ws-product-list__cart-action ws-product-list__cart-action--show cart-button-toggler"
                  (click)="showCart.emit()">
            {{ 'productsList.productCard.showInCart' | translate }}
          </button>
          <button type="button"
                  class="btn ws-button ws-product-list__cart-action ws-product-list__cart-action--add-secondary"
                  (click)="addNew.emit()">
            {{ 'orderProcess.cakesOverview.cakeItem.addNew' | translate }}
          </button>
        </div>
      </div>
    } @else if (showEdit() || showAddNew()) {
      <div class="ws-product-list__cart-actions">
        @if (showEdit()) {
          <button type="button"
                  class="btn ws-button ws-product-list__cart-action ws-product-list__cart-action--edit"
                  (click)="edit.emit()">
            {{ 'common.edit' | translate }}
          </button>
        }
        @if (showAddNew()) {
          <button type="button"
                  class="btn ws-button ws-product-list__cart-action ws-product-list__cart-action--add"
                  (click)="addNew.emit()">
            {{ 'orderProcess.cakesOverview.cakeItem.addNew' | translate }}
          </button>
        }
      </div>
    }
  `,
})
export class ProductCartActionsComponent {
  public readonly buildsCount = input(0);
  public readonly showEdit = input(false);
  public readonly showAddNew = input(false);
  /** Several builds: show this total quantity instead of the number of builds. */
  public readonly totalCount = input<number | null>(null);

  public readonly edit = output<void>();
  public readonly addNew = output<void>();
  public readonly showCart = output<void>();

  public readonly isMultiBuild = computed(() => this.buildsCount() > 1);
}
